import { sanitizeHtml } from '@realadvisor/html-sanitizer';
import { renderToStaticMarkup } from 'react-dom/server';

import {
  htmlToPlainText,
  stripTags,
} from '@modules/_shared/utils/htmlToPlainText';

import { isResolvedBrochureSuggestion } from './brochureSuggestion';
import { escapeAttr, escapeText } from './draftConvertCore';
import {
  draftRawToCanonicalHtml,
  isDraftRawJson,
  makeCustomHtmlMarker,
  makeSnippetMarker,
} from './draftRawToCanonicalHtml';
import { renderPropertySnippet } from './renderPropertySnippet';
import type { EmailEditorSnippetData, EmailEditorSuggestionMap } from './types';
import { normalizeDynamicLinksInHtml, resolveDynamicLinksInHtml } from './utm';

type TFn = (
  key: string,
  options?: Record<string, string | number | null>,
) => string;

type OutputFormat = 'html' | 'plaintext';
type InputFormat = 'html' | 'raw';

const SNIPPET_RE = /<div data-ra-snippet="([^"]*)"><\/div>/g;
const CUSTOM_HTML_RE = /<div data-ra-custom-html="([^"]*)"><\/div>/g;
const SIGNATURE_RE = /<div data-ra-signature="([^"]*)"><\/div>/g;

const SIGNATURE_WRAPPER_STYLE = 'margin-top:15px;color:#4B5563;font-size:14px;';
const SIGNATURE_SEPARATOR_HTML =
  '<div style="height:1px;background-color:#E5E7EB;"></div><div style="height:8px;"></div>';
const BLACKLISTED_TAG_RE = /<\/?(?:script|style|iframe|object|meta)\b[^>]*>/gi;

// The block a sent email carries the signature in (separator rule + grey
// wrapper). Exported so a preview that appends the signature itself renders
// the same block the recipient gets.
export const wrapSignatureHtml = (signatureHtml: string): string =>
  `<div class="ra-email-signature" style="${SIGNATURE_WRAPPER_STYLE}">${SIGNATURE_SEPARATOR_HTML}${signatureHtml}</div>`;

const sanitizeExpandedHtml = (html: string): string =>
  // Pass 1 canonicalizes attribute names; pass 2 enforces their blacklist.
  sanitizeHtml(sanitizeHtml(html)).replace(BLACKLISTED_TAG_RE, '');

export class RichEditorValue {
  private readonly html: string;

  constructor(html: string) {
    this.html = normalizeDynamicLinksInHtml(html);
  }

  getHtml(): string {
    return this.html;
  }

  isEmpty(): boolean {
    return isEmptyHtml(this.html);
  }

  toString(format: OutputFormat, t?: TFn, language?: string): string {
    const expanded = expandMarkers(
      this.html,
      t ?? ((key: string) => key),
      language ?? 'en',
      format === 'html',
    );
    return format === 'html' ? expanded : htmlToPlainText(expanded);
  }

  static createEmpty(): RichEditorValue {
    return new RichEditorValue('');
  }

  static createFromString(
    markup: string,
    format: InputFormat,
  ): RichEditorValue {
    return new RichEditorValue(
      format === 'raw' ? draftRawToCanonicalHtml(markup) : markup,
    );
  }

  static createFromStored(stored: string): RichEditorValue {
    return RichEditorValue.createFromString(
      stored,
      isDraftRawJson(stored) ? 'raw' : 'html',
    );
  }

  static append(prev: RichEditorValue, htmlFragment: string): RichEditorValue {
    return new RichEditorValue(prev.getHtml() + htmlFragment);
  }

  static appendCustomHTML(
    prev: RichEditorValue,
    rawHtml: string,
  ): RichEditorValue {
    return RichEditorValue.append(prev, makeCustomHtmlMarker(rawHtml));
  }

  static withSnippet(
    prev: RichEditorValue,
    data: EmailEditorSnippetData,
  ): RichEditorValue {
    return RichEditorValue.append(prev, makeSnippetMarker(data));
  }

  static updateSuggestions(
    prev: RichEditorValue,
    map: EmailEditorSuggestionMap,
  ): RichEditorValue {
    return new RichEditorValue(resolveVars(prev.getHtml(), map));
  }

  static resolveSuggestions(
    prev: RichEditorValue,
    map: EmailEditorSuggestionMap,
  ): RichEditorValue {
    return new RichEditorValue(
      resolveDynamicLinksInHtml(resolveVars(prev.getHtml(), map), map),
    );
  }
}

const expandMarkers = (
  html: string,
  t: TFn,
  language: string,
  linkBrochures: boolean,
): string => {
  let result = html.replace(SNIPPET_RE, (_match, encoded: string) => {
    const data = JSON.parse(
      decodeURIComponent(encoded),
    ) as EmailEditorSnippetData;
    return renderToStaticMarkup(
      renderPropertySnippet({ ...data, t, language, emailMode: true }),
    );
  });

  result = stripVarSpans(result, t, linkBrochures);

  result = result.replace(CUSTOM_HTML_RE, (_match, encoded: string) =>
    sanitizeExpandedHtml(decodeURIComponent(encoded)),
  );

  result = result.replace(SIGNATURE_RE, (_match, encoded: string) =>
    wrapSignatureHtml(sanitizeExpandedHtml(decodeURIComponent(encoded))),
  );

  return stripVarSpans(result, t, linkBrochures);
};

const replaceVarSpans = (
  html: string,
  render: (span: { id: string; display: string; openTag: string }) => string,
): string => {
  let out = '';
  let cursor = 0;
  const openTagRe = /<span\b(?:[^"'<>]|"[^"]*"|'[^']*')*>/gi;

  for (;;) {
    openTagRe.lastIndex = cursor;
    let match: RegExpExecArray | null = null;
    let encodedId: string | null = null;
    while ((match = openTagRe.exec(html)) != null) {
      encodedId = readEncodedVarId(match[0]);
      if (encodedId != null) {
        break;
      }
    }
    if (match == null || encodedId == null) {
      return out + html.slice(cursor);
    }
    const [openTag] = match;
    const start = match.index;
    const gt = start + openTag.length - 1;
    const close = html.indexOf('</span>', gt);
    if (close === -1) {
      return out + html.slice(cursor);
    }
    out += html.slice(cursor, start);
    out += render({
      id: decodeAttr(encodedId),
      display: html.slice(gt + 1, close),
      openTag,
    });
    cursor = close + '</span>'.length;
  }
};

const readEncodedVarId = (openTag: string): string | null => {
  const attributeRe =
    /([^\s=/>]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g;
  attributeRe.lastIndex = '<span'.length;

  for (;;) {
    const match = attributeRe.exec(openTag);
    if (match == null) {
      return null;
    }
    const [, name, doubleQuoted, singleQuoted, unquoted] = match;
    if (name.toLowerCase() === 'data-ra-var') {
      const value = doubleQuoted ?? singleQuoted ?? unquoted;
      return value ?? null;
    }
  }
};

const stripVarSpans = (html: string, t: TFn, linkBrochures: boolean): string =>
  html
    // Preserve an explicit surrounding link instead of nesting anchors.
    .split(/(<a\b(?:[^"'<>]|"[^"]*"|'[^']*')*>[\s\S]*?<\/a>)/gi)
    .map((part, index) =>
      replaceVarSpans(part, ({ id, display }) => {
        const url = decodeAttr(display);
        if (!linkBrochures || !isResolvedBrochureSuggestion(id, url)) {
          return display;
        }
        const label = escapeText(t('Brochure'));
        return index % 2 === 1
          ? label
          : `<a href="${escapeAttr(url)}">${label}</a>`;
      }),
    )
    .join('');

const resolveVars = (html: string, map: EmailEditorSuggestionMap): string =>
  replaceVarSpans(html, ({ id, display, openTag }) => {
    const suggestion = map[id];
    if (suggestion == null) {
      return `${openTag}${display}</span>`;
    }
    return `${openTag}${escapeText(
      typeof suggestion === 'string' ? suggestion : suggestion.value,
    )}</span>`;
  });

const decodeAttr = (value: string): string =>
  value
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');

const isEmptyHtml = (html: string): boolean => {
  if (html.trim() === '') {
    return true;
  }
  if (
    html.includes('data-ra-snippet') ||
    html.includes('data-ra-custom-html') ||
    html.includes('data-ra-signature') ||
    html.includes('data-ra-var') ||
    /<img\b/i.test(html)
  ) {
    return false;
  }
  return stripTags(html).trim() === '';
};

export type SnippetData = EmailEditorSnippetData;

export { escapeText } from './draftConvertCore';

export const createEmptyValue = (): RichEditorValue =>
  RichEditorValue.createEmpty();

export const createValueFromString = (
  markup: string,
  format: InputFormat,
): RichEditorValue => RichEditorValue.createFromString(markup, format);

export const createValueFromStored = (stored: string): RichEditorValue =>
  RichEditorValue.createFromStored(stored);

export const updateSuggestions = (
  prev: RichEditorValue,
  map: EmailEditorSuggestionMap,
): RichEditorValue => RichEditorValue.updateSuggestions(prev, map);

export const resolveSuggestions = (
  prev: RichEditorValue,
  map: EmailEditorSuggestionMap,
): RichEditorValue => RichEditorValue.resolveSuggestions(prev, map);

export const appendCustomHTML = (
  prev: RichEditorValue,
  html: string,
): RichEditorValue => RichEditorValue.appendCustomHTML(prev, html);

export const appendHtml = (
  prev: RichEditorValue,
  html: string,
): RichEditorValue => RichEditorValue.append(prev, html);

export const withSnippet = (
  prev: RichEditorValue,
  data: EmailEditorSnippetData,
): RichEditorValue => RichEditorValue.withSnippet(prev, data);

export const appendSignature = (
  prev: RichEditorValue,
  signatureHtml: string,
): RichEditorValue =>
  RichEditorValue.append(
    prev,
    '<div data-ra-signature="' + encodeURIComponent(signatureHtml) + '"></div>',
  );

export const removeSignature = (prev: RichEditorValue): RichEditorValue =>
  new RichEditorValue(prev.getHtml().replace(SIGNATURE_RE, ''));

export const hasSignature = (prev: RichEditorValue): boolean =>
  /<div data-ra-signature="/.test(prev.getHtml());
